import { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import {
  Days,
  InformedConsent,
  MinimalPatientResponse,
  PatientBaseSchema,
  PatientMonitoringType,
  SupportedDiagnosis,
  SupportedMedicalData,
  SupportedSymptom,
} from "../api";
import { PatientDto } from "../api/api-connector";

export const paperConsentMonitoringTypes = [
  PatientMonitoringType.physician_supervision,
];
export function canApprovePaperConsent(patient: PatientBaseSchema) {
  if (
    patient.informed_consent === InformedConsent.none &&
    patient.monitoring_type != null &&
    paperConsentMonitoringTypes.includes(patient.monitoring_type)
  ) {
    return true;
  } else {
    return false;
  }
}

export function canRevokePaperConsent(patient: PatientBaseSchema) {
  if (
    patient.informed_consent === InformedConsent.paper &&
    patient.monitoring_type != null &&
    paperConsentMonitoringTypes.includes(patient.monitoring_type)
  ) {
    return true;
  } else {
    return false;
  }
}

export function isPaperConsent(patient: PatientBaseSchema) {
  if (
    patient.monitoring_type != null &&
    paperConsentMonitoringTypes.includes(patient.monitoring_type)
  ) {
    return true;
  } else {
    return false;
  }
}

export function consentOk(patient: PatientBaseSchema) {
  const isPaper = isPaperConsent(patient);
  if (patient.informed_consent === InformedConsent.none) {
    return false;
  } else if (isPaper && patient.informed_consent === InformedConsent.paper) {
    return true;
  } else if (
    !isPaper &&
    patient.informed_consent === InformedConsent.telemonitoring
  ) {
    return true;
  } else {
    return false;
  }
}

export function isPatientDto(
  item: PatientDto | MinimalPatientResponse,
): item is PatientDto {
  return "updated_at" in item;
}

/**
 * Abbreviated weekday names. Descriptors rather than strings: the catalog is
 * only picked at render time, so a module-level string would freeze whichever
 * locale happened to be active when this module first loaded. Pass through
 * `i18n._()` at the call site. Shares msgids with the calendar's week header.
 */
export const dayNames: Record<Days, MessageDescriptor> = {
  [Days.monday]: msg`Mon`,
  [Days.tuesday]: msg`Tue`,
  [Days.wednesday]: msg`Wed`,
  [Days.thursday]: msg`Thu`,
  [Days.friday]: msg`Fri`,
  [Days.saturday]: msg`Sat`,
  [Days.sunday]: msg`Sun`,
};

// data schemas and utils
export type SupportedData =
  | SupportedMedicalData
  | SupportedSymptom
  | SupportedDiagnosis;

export function getNamesValues(variables: SupportedData[]) {
  return variables.map((data) => data.data_name);
}

export function getVariableByName<T extends SupportedData>(
  variables: T[],
  dataName: string,
): T | undefined {
  return variables.find((data) => data.data_name === dataName);
}

export function getEnumValuesByName(
  variables: SupportedData[],
  dataName: string,
) {
  const variable = getVariableByName(variables, dataName);
  if (variable === undefined || variable === null) {
    return [];
  }
  if (variable.data_value.value_type === "enum") {
    return variable.data_value.values_enum;
  } else {
    return [];
  }
}

export function getPrimaryUnitByName(
  variables: SupportedMedicalData[],
  dataName: string,
) {
  const variable = variables.find((data) => data.data_name === dataName);
  if (variable === undefined || variable === null) {
    return null;
  }
  return variable.primary_unit;
}

export function getUnitsValuesByName(
  variables: SupportedMedicalData[],
  dataName: string,
) {
  const variable = variables.find((data) => data.data_name === dataName);
  if (variable === undefined || variable === null) {
    return [];
  }
  if (variable.all_units === null) {
    return [];
  }
  return variable.all_units;
}
